import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState, useRef, useEffect } from 'react';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { FormGroup, Label, Input, InputGroup, InputGroupText } from 'reactstrap';
import { Select, MenuItem, TextField, Checkbox, FormControl, FormControlLabel, IconButton, Chip, InputAdornment, Menu } from '@mui/material';
import TagsManager from '../Tags/TagsManager';

/**
 * PropertyField Component
 * 
 * A reusable component for rendering property fields based on their type.
 * Supports both display and edit modes, and can render using either Material-UI or Reactstrap.
 * 
 * @param {Object} props
 * @param {Object} props.property - The property configuration object
 * @param {string} props.property.name - Property name/key
 * @param {string} props.property.label - Display label
 * @param {string} props.property.type - Property type (text, number, date, select, etc.)
 * @param {Object} props.property.fieldConfig - Field-specific configuration
 * @param {Object} props.property.validation - Validation rules
 * @param {Object} props.property.displayConfig - Display configuration
 * @param {*} props.value - Current value of the field
 * @param {Function} props.onChange - Callback when value changes (fieldName, newValue)
 * @param {string} props.mode - 'edit' or 'display'
 * @param {string} props.library - 'mui' or 'reactstrap' (default: 'reactstrap')
 * @param {boolean} props.editable - Whether field can be edited in display mode
 * @param {Function} props.onStartEdit - Callback when starting inline edit
 * @param {Function} props.onSave - Callback when saving inline edit
 * @param {Function} props.onCancel - Callback when canceling inline edit
 * @param {Object} props.additionalData - Additional data for complex fields (e.g., users list for user type)
 */
const PropertyField = ({ property, value, onChange, mode = 'edit', library = 'reactstrap', editable = false, onStartEdit, onSave, onCancel, additionalData = {}, validators = [], errorMessages = [], hideLabel = false, objectType, objectId }) => {

  if (property?.name === "date") {
    console.log("****property", property);
    console.log("****library", library);
  }

  const inputRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const hasColors = property?.fieldConfig?.hasColors === true || property?.name === 'tags';

  // States for multiselect with add functionality
  const [tagsAnchorEl, setTagsAnchorEl] = useState(null);
  const [localOptions, setLocalOptions] = useState(() => {
    const baseOptions = property?.fieldConfig?.options || [];
    // Initialize with existing tag values if any
    if (hasColors && Array.isArray(value) && value.length > 0) {
      const existingOptions = [];
      value.forEach(val => {
        // Handle both string and object tags
        const tagName = typeof val === 'string' ? val : (val?.name || '');

        if (tagName && !baseOptions.some(opt => opt.value === tagName)) {
          existingOptions.push({
            value: tagName,
            label: tagName.toString().charAt(0).toUpperCase() + tagName.toString().slice(1).replace(/_/g, ' ')
          });
        }
      });
      return [...baseOptions, ...existingOptions];
    }
    return baseOptions;
  });

  useEffect(() => {
    // For tags, keep the full objects to preserve color information
    setTempValue(value);
  }, [value, property?.name]);
  useEffect(() => { if (isEditing && inputRef['current']) inputRef['current'].focus() }, [isEditing]);
  useEffect(() => {
    if (property?.fieldConfig?.options) {
      const baseOptions = property.fieldConfig.options;

      // For tags, ensure all current values are in the options
      if (hasColors && Array.isArray(tempValue) && tempValue.length > 0) {
        const missingOptions = [];
        tempValue.forEach(val => {
          // Handle both string and object tags
          const tagName = typeof val === 'string' ? val : (val?.name || '');

          if (tagName && !baseOptions.some(opt => opt.value === tagName)) {
            missingOptions.push({
              value: tagName,
              label: tagName.toString().charAt(0).toUpperCase() + tagName.toString().slice(1).replace(/_/g, ' ')
            });
          }
        });

        if (missingOptions.length > 0) {
          setLocalOptions([...baseOptions, ...missingOptions]);
        } else {
          setLocalOptions(baseOptions);
        }
      } else {
        setLocalOptions(baseOptions);
      }
    }
  }, [property?.fieldConfig?.options, tempValue, property?.name]);

  const handleStartEdit = () => {
    setIsEditing(true);
    // Keep the full value including tag objects
    setTempValue(value);
    if (onStartEdit) onStartEdit(property['name']);
  };

  const handleSave = () => {
    setIsEditing(false);

    // Pass the tempValue directly (which now includes full tag objects)
    if (onChange) onChange(property['name'], tempValue);
    if (onSave) onSave(property['name'], tempValue);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setTempValue(value);
    if (onCancel) onCancel(property['name']);
  };

  const handleKeyPress = (e) => {
    if (e['key'] === 'Enter' && property['type'] !== 'textarea') handleSave();
    else if (e['key'] === 'Escape') handleCancel();
  };

  // Get field configuration
  const fieldConfig = property['fieldConfig'] || {};
  const validation = property['validation'] || {};
  const displayConfig = property['displayConfig'] || {};

  // Format value for display
  // Convert plain text URLs to clickable links
  const convertUrlsToLinks = (text) => {
    if (!text || typeof text !== 'string') return text;
    const parts = text.split(/(https?:\/\/[^\s<>"{}|\\^`[\]]+|www\.[^\s<>"{}|\\^`[\]]+)/gi);
    return parts.length === 1 ? text : parts.filter(Boolean).map((part, i) =>
      /^(https?:\/\/|www\.)/i.test(part) ? (
        <a key={i} href={part.startsWith('http') ? part : `https://${part}`} target="_blank" rel="noopener noreferrer" className="description-link" onClick={(e) => e.stopPropagation()}>
          {part}
        </a>
      ) : part
    );
  };

  const formatDisplayValue = (val) => {
    if (val === null || val === undefined || val === '') return '-';

    switch (property['type']) {
      case 'currency':
        const currencyCode = fieldConfig['currencyCode'] || 'USD';
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currencyCode
        }).format(val);

      case 'percentage':
        return `${val}%`;

      case 'date':
        return new Date(val).toLocaleDateString();

      case 'datetime':
        return new Date(val).toLocaleString();

      case 'boolean':
        return val ? 'Yes' : 'No';

      case 'multiselect':
        if (Array.isArray(val)) {
          // Special handling for tags property - render as colored chips
          if (hasColors) {
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {val.map((tag, index) => {
                  const tagName = typeof tag === 'object' && tag !== null ? (tag.name || tag.label || 'Unnamed Tag') : tag;
                  const tagColor = typeof tag === 'object' && tag !== null ? (tag.color || '#6B7280') : '#6B7280';

                  return (
                    <span
                      key={index}
                      style={{ backgroundColor: tagColor, color: '#FFFFFF', padding: '2px 6px', borderRadius: '6px', fontSize: '10px', fontWeight: 500, display: 'inline-block', whiteSpace: 'nowrap' }}
                    >
                      {tagName}
                    </span>
                  );
                })}
              </div>
            );
          }

          // Regular multiselect handling for other properties
          return val.map(v => {
            const option = localOptions.find(opt => opt['value'] === v) || fieldConfig['options']?.find(opt => opt['value'] === v);
            if (option) return option['label'] || v;
            if (typeof v === 'object' && v !== null) return v.name || v.label || JSON.stringify(v);
            return v;
          }).join(', ');
        }
        return '-';

      case 'select':
        const option = fieldConfig['options']?.find(opt => opt['value'] === val);
        return option?.['label'] || val;

      case 'tags':
        if (Array.isArray(val)) {
          return val.map(tag => {
            // Handle both object format { name, color } and string format
            if (typeof tag === 'object' && tag !== null) {
              return tag.name || tag.label || 'Unnamed Tag';
            }
            return tag;
          }).join(', ');
        }
        // Handle single tag object
        if (typeof val === 'object' && val !== null) {
          return val.name || val.label || 'Unnamed Tag';
        }
        return val || '-';

      case 'user':
        if (typeof val === 'object' && val !== null) return val['firstName'] ? `${val['firstName']} ${val['lastName']}` : val['email'];
        const user = additionalData.users?.find(u => u['_id'] === val);
        return user ? (user['firstName'] ? `${user['firstName']} ${user['lastName']}` : user['email']) : val;

      case 'contact':
        if (typeof val === 'object' && val !== null) return val['firstName'] ? `${val['firstName']} ${val['lastName']}` : val['email'];
        return val;

      case 'company':
        if (typeof val === 'object' && val !== null) return val['name'] || val;
        return val;

      case 'reference':
        // Handle reference type - it might be an object with _id or just an ID string
        if (typeof val === 'object' && val !== null) {
          // If it has a name or title property, use that
          if (val['name']) return val['name'];
          if (val['title']) return val['title'];
          // Otherwise, return the ID if available
          if (val['_id']) return val['_id'];
          // As a last resort, return a string representation
          return JSON.stringify(val);
        }
        return val || '-';

      case 'email':
      case 'phone':
      case 'url':
      case 'text':
      case 'textarea':
      default:
        // Ensure we never return an object for display
        if (typeof val === 'object' && val !== null) {
          console.warn(`Unexpected object value for property ${property['name']} of type ${property['type']}:`, val);
          return JSON.stringify(val);
        }
        // Convert URLs to clickable links for text and textarea fields
        if ((property['type'] === 'text' || property['type'] === 'textarea' || property['type'] === 'url') && typeof val === 'string') {
          return convertUrlsToLinks(val);
        }
        return val;
    }
  };

  // Render field based on type and mode
  const renderField = () => {

    // Display mode (non-editing)
    if (mode === 'display' && !isEditing) {
      // When hideLabel is true, just show the value
      if (hideLabel) {
        // Special handling for tags to keep edit icon inline
        if (hasColors) {
          return (
            <div
              className={`property-value-display ${editable ? 'editable' : ''}`}
              style={{ cursor: editable ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {formatDisplayValue(value)}
              {editable && !displayConfig['readOnly'] && (
                <IconButton size="small" onClick={handleStartEdit} style={{ flexShrink: 0 }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </div>
          );
        }

        return (
          <div
            className={`property-value-display ${editable ? 'editable' : ''}`}
            onClick={editable ? handleStartEdit : undefined}
            style={{ cursor: editable ? 'pointer' : 'default' }}
          >
            {formatDisplayValue(value)}
            {editable && !displayConfig['readOnly'] && (
              <IconButton size="small" onClick={handleStartEdit} style={{ marginLeft: '3px' }}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        );
      }

      // Original behavior with label
      return (
        <div className={`deal-box-owner ${fieldConfig?.rows || property?.type === 'textarea' ? 'multiline' : ''}`} onClick={editable ? handleStartEdit : undefined} style={{ cursor: editable ? 'pointer' : 'default' }}>
          <p onClick={editable ? handleStartEdit : undefined}>
            <strong>{property['label']}:</strong>
          </p>
          <div className='right-text-area' style={{ display: 'flex', alignItems: 'center', gap: '8px' }} onClick={editable ? handleStartEdit : undefined}>
            {formatDisplayValue(value)}
            {editable && !displayConfig['readOnly'] && (
              <IconButton size="small" onClick={handleStartEdit} style={{ flexShrink: 0 }}>
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        </div>
      );
    }

    // Display mode (editing) - when hideLabel is true, parent shows the label
    // Just render the input without extra wrappers to preserve parent's layout
    if (mode === 'display' && isEditing && hideLabel) {
      // Continue to render the edit field below (falls through to switch statement)
    }

    // Edit mode rendering
    switch (property['type']) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
        const inputType = property['type'] === 'email' ? 'email' :
          property['type'] === 'phone' ? 'tel' :
            property['type'] === 'url' ? 'url' : 'text';

        if (library === 'mui') {
          return (
            <TextField
              ref={inputRef}
              fullWidth
              type={inputType}
              // place={property.label}
              value={tempValue || ''}
              onChange={(e) => {
                const newValue = e['target']['value'];
                setTempValue(newValue);
                if (mode === 'edit' && onChange) onChange(property['name'], newValue);
              }}
              onKeyDown={handleKeyPress}
              onBlur={isEditing ? handleSave : undefined}
              required={validation['required']}
              placeholder={fieldConfig['placeholder']}
              inputProps={{
                minLength: fieldConfig['minLength'],
                maxLength: fieldConfig['maxLength'],
                pattern: validation['pattern']
              }}
              size="small"
              error={validators.length > 0}
              helperText={errorMessages[0]}
            />
          );
        }

        return (
          <Input
            ref={inputRef}
            type={inputType}
            value={tempValue || ''}
            onChange={(e) => {
              const newValue = e['target']['value'];
              setTempValue(newValue);
              if (mode === 'edit' && onChange) onChange(property['name'], newValue);
            }}
            onKeyDown={handleKeyPress}
            onBlur={isEditing ? handleSave : undefined}
            required={validation['required']}
            placeholder={fieldConfig['placeholder']}
            minLength={fieldConfig['minLength']}
            maxLength={fieldConfig['maxLength']}
            pattern={validation['pattern']}
          />
        );

      case 'textarea':
        if (library === 'mui') {
          return (
            <TextField
              ref={inputRef}
              fullWidth
              multiline
              rows={fieldConfig['rows'] || 3}
              // label={property.label}
              value={tempValue || ''}
              onChange={(e) => {
                const newValue = e['target']['value'];
                setTempValue(newValue);
                if (mode === 'edit' && onChange) onChange(property['name'], newValue);
              }}
              onKeyDown={handleKeyPress}
              onBlur={isEditing ? handleSave : undefined}
              required={validation['required']}
              placeholder={fieldConfig['placeholder']}
              inputProps={{
                minLength: fieldConfig.minLength,
                maxLength: fieldConfig.maxLength
              }}
              error={validators.length > 0}
              helperText={errorMessages[0]}
            />
          );
        }

        return (
          <Input
            ref={inputRef}
            type="textarea"
            value={tempValue || ''}
            onChange={(e) => {
              const newValue = e['target']['value'];
              setTempValue(newValue);
              if (mode === 'edit' && onChange) onChange(property['name'], newValue);
            }}
            onKeyDown={handleKeyPress}
            onBlur={isEditing ? handleSave : undefined}
            required={validation['required']}
            placeholder={fieldConfig['placeholder']}
            minLength={fieldConfig['minLength']}
            maxLength={fieldConfig['maxLength']}
            rows={fieldConfig.rows || 3}
          />
        );

      case 'number':
      case 'currency':
      case 'percentage':
        if (library === 'mui') {
          return (
            <TextField
              ref={inputRef}
              fullWidth
              type="text"
              placeholder={property.label}
              value={tempValue || ''}
              onChange={(e) => {
                let raw = e['target']['value'] || '';
                raw = raw.replace(/[^0-9.]/g, '');
                const parts = raw.split('.');
                const sanitized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : raw;
                setTempValue(sanitized);
                if (mode === 'edit' && onChange) onChange(property['name'], sanitized);
              }}
              onKeyDown={handleKeyPress}
              onBlur={isEditing ? handleSave : undefined}
              required={validation['required']}
              inputProps={{
                inputMode: 'decimal',
                pattern: '[0-9.,]*'
              }}
              InputProps={{
                startAdornment: (property['type'] === 'currency' && fieldConfig['currencyPosition'] === 'start') ? <InputAdornment position="start">{fieldConfig['currencyCode'] || 'USD'}</InputAdornment> : undefined,
                endAdornment: (property['type'] === 'currency' && fieldConfig['currencyPosition'] !== 'start') ? <InputAdornment position="end">{fieldConfig['currencyCode'] || 'USD'}</InputAdornment> : (property['type'] === 'percentage' ? <InputAdornment position="end">%</InputAdornment> : undefined)
              }}
              sx={{
                '& input': {
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                  paddingRight: (property['type'] === 'currency' || property['type'] === 'percentage') ? '48px' : undefined
                }
              }}
              size="small"
              error={validators.length > 0}
              helperText={errorMessages[0]}
            />
          );
        }

        return (
          <InputGroup>
            {property['type'] === 'currency' && fieldConfig['currencyPosition'] === 'start' && (
              <InputGroupText>{fieldConfig['currencyCode'] || 'USD'}</InputGroupText>
            )}
            <Input
              ref={inputRef}
              type="text"
              value={tempValue || ''}
              onChange={(e) => {
                let raw = e['target']['value'] || '';
                raw = raw.replace(/[^0-9.]/g, '');
                const parts = raw.split('.');
                const sanitized = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : raw;
                setTempValue(sanitized);
                if (mode === 'edit' && onChange) onChange(property['name'], sanitized);
              }}
              onKeyDown={handleKeyPress}
              onBlur={isEditing ? handleSave : undefined}
              required={validation['required']}
              inputMode="decimal"
              pattern="[0-9.,]*"
              style={{ paddingRight: (property['type'] === 'currency' || property['type'] === 'percentage') ? '56px' : undefined, overflowX: 'auto', whiteSpace: 'nowrap' }}
            />
            {property['type'] === 'currency' && fieldConfig['currencyPosition'] !== 'start' && (
              <InputGroupText>{fieldConfig['currencyCode'] || 'USD'}</InputGroupText>
            )}
            {property['type'] === 'percentage' && (
              <InputGroupText>%</InputGroupText>
            )}
          </InputGroup>
        );

      case 'date':
      case 'datetime':
        if (library === 'mui') {
          const DateComponent = property['type'] === 'datetime' ? DateTimePicker : DatePicker;
          return (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateComponent
                placeholder={property.label}
                value={tempValue ? new Date(tempValue) : null}
                className="date-piker"
                onChange={(newValue) => {
                  // For date fields (not datetime), store at midnight UTC to avoid timezone issues
                  let isoValue;
                  if (property['type'] === 'date' && newValue) {
                    // Get year, month, day and create a UTC date at midnight
                    const year = newValue.getFullYear();
                    const month = newValue.getMonth();
                    const day = newValue.getDate();
                    isoValue = new Date(Date.UTC(year, month, day, 0, 0, 0, 0)).toISOString();
                  } else isoValue = newValue?.toISOString();

                  setTempValue(isoValue);
                  if (mode === 'edit' && onChange) onChange(property['name'], isoValue);

                  // For display mode, trigger save immediately on date selection
                  if (mode === 'display' && isEditing) {
                    setIsEditing(false);
                    if (onChange) onChange(property['name'], isoValue);
                    if (onSave) onSave(property['name'], isoValue);
                  }
                }}
                onClose={() => { if (isEditing) setIsEditing(false) }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    required={validation['required']}
                    error={validators.length > 0}
                    helperText={errorMessages[0]}
                  />
                )}
                minDate={fieldConfig['minDate'] ? new Date(fieldConfig['minDate']) : undefined}
                maxDate={fieldConfig['maxDate'] ? new Date(fieldConfig['maxDate']) : undefined}
              />
            </LocalizationProvider>
          );
        }

        // Helper functions to handle datetime splitting and combining
        const getDateValue = (isoString) => {
          if (!isoString) return '';
          const date = new Date(isoString);
          return date.toISOString().split('T')[0];
        };

        const getTimeValue = (isoString) => {
          if (!isoString) return '';
          const date = new Date(isoString);
          return date.toTimeString().split(' ')[0].substring(0, 5); // HH:MM format
        };

        const combineDateTimeValues = (dateValue, timeValue) => {
          if (!dateValue) return '';
          const combinedValue = timeValue ? `${dateValue}T${timeValue}:00` : `${dateValue}T00:00:00`;
          return new Date(combinedValue).toISOString();
        };

        if (property['type'] === 'datetime') {
          // For datetime, show separate date and time inputs
          const currentDateValue = getDateValue(tempValue);
          const currentTimeValue = getTimeValue(tempValue);

          return (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '140px' }}>
                <Input
                  type="date"
                  value={currentDateValue}
                  onChange={(e) => {
                    const newDateValue = e['target']['value'];
                    const newValue = combineDateTimeValues(newDateValue, currentTimeValue);
                    setTempValue(newValue);
                    if (mode === 'edit' && onChange) onChange(property['name'], newValue);
                  }}
                  onKeyDown={handleKeyPress}
                  onBlur={isEditing ? handleSave : undefined}
                  required={validation['required']}
                  min={fieldConfig['minDate']}
                  max={fieldConfig['maxDate']}
                />
              </div>
              <div style={{ flex: 1, minWidth: '100px' }}>
                <Input
                  type="time"
                  value={currentTimeValue}
                  onChange={(e) => {
                    const newTimeValue = e['target']['value'];
                    const newValue = combineDateTimeValues(currentDateValue, newTimeValue);
                    setTempValue(newValue);
                    if (mode === 'edit' && onChange) onChange(property['name'], newValue);
                  }}
                  onKeyDown={handleKeyPress}
                  onBlur={isEditing ? handleSave : undefined}
                />
              </div>
            </div>
          );
        } else {
          // For date only, use single date input
          return (
            <Input
              ref={inputRef}
              type="date"
              value={tempValue ? getDateValue(tempValue) : ''}
              onChange={(e) => {
                const newValue = e['target']['value'] ? new Date(e['target']['value']).toISOString() : '';
                setTempValue(newValue);
                if (mode === 'edit' && onChange) onChange(property['name'], newValue);
              }}
              onKeyDown={handleKeyPress}
              onBlur={isEditing ? handleSave : undefined}
              required={validation['required']}
              min={fieldConfig['minDate']}
              max={fieldConfig['maxDate']}
            />
          );
        }

      case 'select':
        if (library === 'mui') {
          return (
            <Select
              fullWidth
              value={tempValue || ''}
              onChange={(e) => {
                const newValue = e['target']['value'];
                setTempValue(newValue);
                onChange(property['name'], newValue);
              }}
              onClose={isEditing ? handleCancel : undefined}
              size="small"
              displayEmpty
              required={validation['required']}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {fieldConfig['options']?.map(option => (
                <MenuItem key={option['value']} value={option['value']}>
                  {option['label']}
                </MenuItem>
              ))}
            </Select>
          );
        }

        return (
          <Input
            ref={inputRef}
            type="select"
            value={tempValue || ''}
            onChange={(e) => {
              const newValue = e['target']['value'];
              setTempValue(newValue);
              if (mode === 'edit' && onChange) onChange(property['name'], newValue);
              if (isEditing) handleSave();
            }}
            required={validation['required']}
          >
            <option value="">Select {property['label']}</option>
            {fieldConfig['options']?.map(option => (
              <option key={option['value']} value={option['value']}>
                {option['label']}
              </option>
            ))}
          </Input>
        );

      case 'multiselect':
        if (library === 'mui') {
          const handleAddNewOption = () => {
            const trimmedValue = newOptionValue.trim();

            // Don't add empty tags
            if (!trimmedValue) {
              setIsAddingNew(false);
              setNewOptionValue('');
              return;
            }

            // Check if option already exists (check both value and label)
            const normalizedValue = trimmedValue.toLowerCase().replace(/\s+/g, '_');
            if (localOptions.some(opt => opt['value'] === normalizedValue || opt['value'] === trimmedValue || opt['label'] === trimmedValue)) {
              setIsAddingNew(false);
              setNewOptionValue('');
              return;
            }

            // Create new option
            const newOption = {
              value: normalizedValue,
              label: trimmedValue
            };

            // Add to local options
            const updatedOptions = [...localOptions, newOption];
            setLocalOptions(updatedOptions);

            // Add the new value to selected values
            const newValues = [...(Array.isArray(tempValue) ? tempValue : []), newOption.value];
            setTempValue(newValues);
            if (mode === 'edit' && onChange) onChange(property['name'], newValues);

            // For tags, we don't need to update the property config
            // Just save the new values

            setIsAddingNew(false);
            setNewOptionValue('');

            // Keep the dropdown open by preventing default close behavior
            // The dropdown will remain open for further selections
          };

          return (
            <>
              <Select
                fullWidth
                multiple
                value={Array.isArray(tempValue) ? tempValue.map(tag =>
                  // For Select, we need tag names as values
                  typeof tag === 'string' ? tag : (tag?.name || '')
                ) : []}
                onChange={(e) => {
                  const selectedNames = e['target']['value'];
                  // Filter out empty values
                  const filteredNames = Array.isArray(selectedNames)
                    ? selectedNames.filter(v => v !== '' && v !== '__adding_new__')
                    : selectedNames;

                  if (hasColors) {
                    // Reconstruct tag objects preserving colors for existing tags
                    const updatedTags = filteredNames.map(name => {
                      // Find existing tag to preserve its properties
                      const existingTag = Array.isArray(tempValue) ? tempValue.find(tag => {
                        if (typeof tag === 'string') return tag === name;
                        return tag?.name === name;
                      }) : null;

                      // Return existing tag (preserves color) or name for new/old string tags
                      return existingTag || name;
                    });

                    setTempValue(updatedTags);
                    if (mode === 'edit' && onChange) {
                      onChange(property['name'], updatedTags);
                    }
                  } else {
                    setTempValue(filteredNames);
                    if (mode === 'edit' && onChange) onChange(property['name'], filteredNames);
                  }
                }}
                onClose={() => {
                  // In display mode, save when closing the dropdown
                  if (mode === 'display' && isEditing) {
                    if (hasColors) setIsEditing(false);
                    else if (onSave) handleSave();
                  }
                }}
                size="small"
                displayEmpty
                renderValue={(selected) => {
                  // Use tempValue directly to preserve full tag objects with colors
                  const tagsToDisplay = Array.isArray(tempValue) ? tempValue : [];
                  if (tagsToDisplay.length === 0) return <em>None selected</em>;

                  return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {tagsToDisplay.map((val, index) => {
                        // Handle both old (string) and new (object) tag formats
                        const tagName = typeof val === 'string' ? val : (val?.name || 'Unnamed Tag');
                        const tagColor = typeof val === 'string'
                          ? '#6B7280'  // Default gray for old string tags
                          : (val?.color || '#6B7280'); // Use tag color or default if missing

                        return (
                          <span
                            key={tagName}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              height: '24px',
                              backgroundColor: tagColor,
                              color: '#FFFFFF',
                              borderRadius: '16px',
                              border: `1px solid ${tagColor}40`,
                              padding: '0 4px 0 8px',
                              fontSize: '11px',
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                            }}
                            onMouseDown={(event) => { event.stopPropagation(); event.preventDefault(); }}
                          >
                            {tagName}
                            <span
                              role="button"
                              style={{
                                marginLeft: '4px',
                                cursor: 'pointer',
                                color: '#FFFFFF',
                                fontSize: '16px',
                                lineHeight: 1,
                                display: 'inline-flex',
                                alignItems: 'center',
                              }}
                              onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                setTempValue(prevTempValue => {
                                  const currentTags = Array.isArray(prevTempValue) ? prevTempValue : [];
                                  const newValues = currentTags.filter(v => {
                                    const vName = typeof v === 'string' ? v : (v?.name || '');
                                    const valName = typeof val === 'string' ? val : (val?.name || '');
                                    return vName !== valName;
                                  });
                                  if (onChange) onChange(property['name'], newValues);
                                  return newValues;
                                });
                              }}
                            >
                              ×
                            </span>
                          </span>
                        );
                      })}
                    </div>
                  );
                }}
                MenuProps={{
                  PaperProps: {
                    className: 'custom-dropdown-menu-tags style-two'
                  },
                }}
              >
                {/* For tags field, don't show options in dropdown, only show Add new tag */}
                {!hasColors && localOptions.map(option => (
                  <MenuItem key={option['value']} value={option['value']}>
                    {option['label']}
                  </MenuItem>
                ))}
                <MenuItem
                  value=""
                  disabled={false}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  className='custom-dropdown-menu-tags'
                  style={{ borderTop: '1px solid #e0e0e0', color: '#1976d2', fontWeight: 500, cursor: 'default', padding: 0 }}
                >
                  <TagsManager
                    objectType={objectType || 'deals'}
                    mode="select"
                    selectedObjects={objectId ? [objectId] : []}
                    selectedTags={Array.isArray(tempValue) ? tempValue : []}
                    onClose={() => setTagsAnchorEl(null)}
                    onTagsChange={(tags) => {
                      // Optimistic local update - immediately show changes in UI
                      setTempValue(tags);
                      if (onChange) onChange(property['name'], tags);
                    }}
                    onTagsApplied={() => {
                      // The tags have been applied via bulk update in the backend
                      // We need to refresh the data from parent component
                      setTagsAnchorEl(null);

                      // Call onSave with a special flag to trigger a full refresh
                      if (onSave) {
                        onSave('tags', null, true); // Pass true to indicate need for refresh
                      }
                    }}
                  />
                </MenuItem>

                {/* Add new tag menu item - only for tags field */}
                {/* {hasColors && (
                  <MenuItem
                    value=""
                    disabled={false}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setTagsAnchorEl(e.currentTarget);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    style={{
                      borderTop: '1px solid #e0e0e0',
                      color: '#1976d2',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    <AddIcon fontSize="small" style={{ marginRight: 8 }} />
                    Add new tag
                  </MenuItem>
                )} */}
              </Select>

              {/* TagsManager dropdown for adding tags with colors */}
              {/* {hasColors && (
                <Menu
                  anchorEl={tagsAnchorEl}
                  open={Boolean(tagsAnchorEl)}
                  onClose={() => setTagsAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <TagsManager
                    objectType={objectType || 'deals'}
                    mode="select"
                    selectedObjects={objectId ? [objectId] : []}
                    onClose={() => setTagsAnchorEl(null)}
                    onTagsApplied={() => {
                      // The tags have been applied via bulk update in the backend
                      // We need to refresh the data from parent component
                      setTagsAnchorEl(null);

                      // Call onSave with a special flag to trigger a full refresh
                      if (onSave) {
                        onSave('tags', null, true); // Pass true to indicate need for refresh
                      }
                    }}
                  />
                </Menu>
              )} */}
            </>
          );
        }

        // For reactstrap with hasColors (tags), use TagsManager in a dropdown
        if (hasColors) {
          const selectedArr = Array.isArray(tempValue) ? tempValue : [];
          // selectedArr contains [{name, color}] objects from TagsManager
          const selectedTagNames = selectedArr.map(t => typeof t === 'string' ? t : t?.name || '');
          return (
            <>
              <FormControl>
                <Select
                  displayEmpty
                  open={false}
                  value=""
                  onClick={(e) => setTagsAnchorEl(e.currentTarget)}
                  renderValue={() => {
                    if (selectedArr.length === 0) return <em style={{ color: '#aaa' }}>Select tags</em>;
                    return (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {selectedArr.map((tag, idx) => {
                          const tagName = typeof tag === 'string' ? tag : tag?.name || '';
                          const tagColor = typeof tag === 'string' ? null : tag?.color;
                          return (
                            <Chip
                              key={tagName || idx}
                              size="small"
                              label={tagName}
                              sx={tagColor ? {
                                backgroundColor: tagColor + '20',
                                color: tagColor,
                                border: `1px solid ${tagColor}`,
                                fontWeight: 500,
                                height: 22
                              } : { height: 22 }}
                            />
                          );
                        })}
                      </div>
                    );
                  }}
                />
              </FormControl>
              <Menu
                anchorEl={tagsAnchorEl}
                open={Boolean(tagsAnchorEl)}
                onClose={() => setTagsAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { className: 'custom-dropdown-menu-tags' } }}
              >
                <TagsManager
                  objectType={property['objectType'] || objectType}
                  mode="form"
                  selectedTags={selectedTagNames}
                  onTagsChange={(tags) => {
                    setTempValue(tags);
                    if (mode === 'edit' && onChange) onChange(property['name'], tags);
                  }}
                />
              </Menu>
            </>
          );
        }

        // For reactstrap, use MUI multi-select dropdown
        return (
          <FormControl>
            <Select
              displayEmpty
              multiple
              value={Array.isArray(tempValue) ? tempValue : []}
              onChange={(e) => {
                const newValue = e['target']['value'];
                setTempValue(newValue);
                if (mode === 'edit' && onChange) onChange(property['name'], newValue);
              }}
              renderValue={(selected) => {
                if (!selected || selected.length === 0) return <em style={{ color: '#aaa' }}>Select options</em>;
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {selected.map(val => {
                      const opt = fieldConfig['options']?.find(o => o['value'] === val);
                      return (
                        <Chip
                          key={val}
                          size="small"
                          label={opt ? opt['label'] : val}
                          sx={{ height: 22 }}
                        />
                      );
                    })}
                  </div>
                );
              }}
              MenuProps={{ disableAutoFocusItem: true }}
            >
              {fieldConfig['options']?.map(option => (
                <MenuItem key={option['value']} value={option['value']}>
                  <Checkbox checked={Array.isArray(tempValue) && tempValue.includes(option['value'])} size="small" />
                  {option['label']}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'boolean':
        if (library === 'mui') {
          return (
            <FormControlLabel
              control={
                <Checkbox
                  checked={tempValue === true || tempValue === 'true'}
                  onChange={(e) => {
                    const newValue = e['target']['checked'];
                    setTempValue(newValue);
                    if (mode === 'edit' && onChange) onChange(property['name'], newValue);
                    if (isEditing) handleSave();
                  }}
                  required={validation['required']}
                />
              }
              label={property.label}
            />
          );
        }

        return (
          <FormGroup check>
            <Label check>
              <Input
                type="checkbox"
                checked={tempValue === true || tempValue === 'true'}
                onChange={(e) => {
                  const newValue = e['target']['checked'];
                  setTempValue(newValue);
                  if (mode === 'edit' && onChange) onChange(property['name'], newValue);
                  if (isEditing) handleSave();
                }}
              />
              {' '}{property.label}
            </Label>
          </FormGroup>
        );

      case 'user':
        const users = additionalData.users || [];
        if (library === 'mui') {
          return (
            <Select
              fullWidth
              value={tempValue?.['_id'] || tempValue || ''}
              onChange={(e) => {
                const selectedUser = users.find(u => u['_id'] === e['target']['value']);
                setTempValue(selectedUser);
                onChange(property['name'], selectedUser);
              }}
              onClose={isEditing ? handleCancel : undefined}
              size="small"
              displayEmpty
              required={validation['required']}
              renderValue={(value) => {
                if (!value) return <em>None</em>;
                const user = users.find(u => u['_id'] === value) || tempValue;
                return user?.firstName ? `${user['firstName']} ${user['lastName']}` : user?.email || value;
              }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {users.map(user => (
                <MenuItem key={user['_id']} value={user['_id']}>
                  {user['firstName'] ? `${user['firstName']} ${user['lastName']}` : user['email']}
                </MenuItem>
              ))}
            </Select>
          );
        }

        return (
          <Input
            type="select"
            value={tempValue?.['_id'] || tempValue || ''}
            onChange={(e) => {
              const selectedUser = users.find(u => u['_id'] === e['target']['value']);
              setTempValue(selectedUser);
              if (mode === 'edit' && onChange) onChange(property['name'], selectedUser);
              if (isEditing) handleSave();
            }}
            required={validation['required']}
          >
            <option value="">Select {property['label']}</option>
            {users.map(user => (
              <option key={user['_id']} value={user['_id']}>
                {user['firstName'] ? `${user['firstName']} ${user['lastName']}` : user['email']}
              </option>
            ))}
          </Input>
        );

      case 'contact':
      case 'company':
      case 'reference':
        // These would need specific implementations based on your data structure
        // For now, using a simple text input
        return renderField({ ...property, type: 'text' });

      case 'file':
        return (
          <Input
            type="file"
            onChange={(e) => {
              const file = e['target']['files'][0];
              setTempValue(file);
              if (onChange) onChange(property['name'], file);
            }}
            accept={fieldConfig['allowedExtensions']?.map(ext => `.${ext}`).join(',')}
          />
        );

      default:
        return renderField({ ...property, type: 'text' });
    }
  };

  // Main render
  if (mode === 'display' && !isEditing && !editable) return (
    <div className="property-field-display">
      <span>{property['label']}: <strong>{formatDisplayValue(value)}</strong></span>
    </div>
  );

  if (library === 'mui' && (property['type'] === 'checkbox' || property['type'] === 'boolean')) return renderField();

  // When in display mode with isEditing true
  if (mode === 'display' && isEditing) {
    // If hideLabel is true, parent component shows the label, so just return the field
    if (hideLabel) {
      return (
        <div className="property-field-editing-wrapper" style={{ width: '100%', maxWidth: '100%', margin: 0, padding: 0 }}>
          {renderField()}
        </div>
      );
    }

    // If hideLabel is false/undefined, we need to show the label ourselves
    // Preserve the deal-box-owner structure to keep the label visible
    return (
      <div className={`deal-box-owner edit-owner ${fieldConfig?.rows || property?.type === 'textarea' ? 'multiline' : ''}`}>
        <p>
          <strong>{property['label']}:</strong>
        </p>
        <div className='right-text-area' style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
          {renderField()}
        </div>
      </div>
    );
  }

  return (
    <FormGroup>
      {mode === 'edit' && property['type'] !== 'checkbox' && property['type'] !== 'boolean' && (
        <Label>
          {property.label}
          {validation['required'] && ' *'}
        </Label>
      )}
      {renderField()}
      {isEditing && mode === 'display' && (
        <div style={{ marginTop: '3px' }}>
          {/* <button className='save-btn-click' onClick={handleSave}>Save</button>
          <button className='save-btn-click cancel-click' onClick={handleCancel}>Cancel</button> */}
          {/* <IconButton className='edt-save-btn' size="small"  color="primary">
            <SaveIcon fontSize="small" className='save-icon' />
          </IconButton> */}
          {/* <IconButton className='edt-save-btn' size="small" onClick={handleCancel} color="secondary">
            <CancelIcon fontSize="small" className='edit-icon' />
          </IconButton> */}
        </div>
      )}
      {displayConfig['helpText'] && mode === 'edit' && (
        <small className="text-muted">{displayConfig['helpText']}</small>
      )}
    </FormGroup>
  );
};

export default PropertyField;